import React, { createContext, useContext, PropsWithChildren } from 'react'
import { gql } from 'apollo-boost'

import {
  InvoiceAPI,
  InvoiceResponses,
  InvoiceDetails,
  Invoice,
  InvoiceStatus,
  InvoiceOrdersResponse,
  InvoiceableResponse,
  ACHStatus,
  ACHLog,
} from './model'
import * as R from 'ramda'

import oauthClient from 'api/auth'

interface InvoiceAPIProps {
  svc: string
}

const makeQueryString = (query: any, baseUrl: string): string =>
  R.toPairs(query)
    .filter(([_k, v]: [string, string]) => v != null)
    .reduce((str, [k, v], index) => str + `${index > 0 ? '&' : ''}${k}=${encodeURIComponent(v as string)}`, baseUrl)

const authJsonHeaders = (token: string): Record<string, string> => {
  return {
    'Content-Type': 'application/json',
    Authorisation: `Bearer ${token}`,
    Authorization: `Bearer ${token}`,
  }
}

const authUploadJsonHeaders = (token: string): Record<string, string> => {
  return {
    Authorisation: `Bearer ${token}`,
    Authorization: `Bearer ${token}`,
  }
}

const Ctx = createContext<InvoiceAPI | null>(null)
export function InvoiceAPICtx(props: PropsWithChildren<InvoiceAPIProps>): JSX.Element {
  const token = oauthClient.getAuthResponse() ? oauthClient.getAuthResponse().access_token : null
  const invoiceAPI = {
    hasInvoices: () => Promise.resolve(false),
    getDetails: (invoiceId: number): Promise<InvoiceDetails> =>
      fetch(props.svc + `/v5/invoices/${invoiceId}/summary`, {
        headers: authJsonHeaders(token),
        method: 'GET',
      }).then((res) => res.json()),
    getInvoiceables: (invoiceId: number, page: number, per_page = 10): Promise<InvoiceableResponse> =>
      fetch(props.svc + `/v5/invoices/${invoiceId}/billables?page=${page}&per_page=${per_page}`, {
        headers: authJsonHeaders(token),
        method: 'GET',
      }).then((res) => res.json()),
    getInvoiceablesExcept: (
      invoiceId: number,
      invoiceableType: string,
      page: number,
      per_page: number
    ): Promise<InvoiceableResponse> =>
      fetch(
        props.svc +
          `/v5/invoices/${invoiceId}/billables?page=${page}&per_page=${per_page}&billable_type%5Bnot%5D=${encodeURIComponent(
            invoiceableType
          )}`,
        {
          headers: authJsonHeaders(token),
        }
      ).then((res) => res.json()),
    getInvoiceableSummary: (
      invoiceId: number,
      invoiceableId: string,
      invoiceableType: string
    ): Promise<InvoiceDetails> =>
      fetch(
        props.svc +
          `/v5/invoices/${invoiceId}/summary?billable_id=${encodeURIComponent(
            invoiceableId
          )}&billable_type=${encodeURIComponent(invoiceableType)}`,
        { headers: authJsonHeaders(token), method: 'GET' }
      ).then((res) => res.json()),
    getInvoice: (invoiceId: number): Promise<Invoice> =>
      fetch(props.svc + `/v5/invoices/${invoiceId}`, { headers: authJsonHeaders(token) }).then((res) => res.json()),
    //.then((data) => Promise.resolve({ ...data, simplified_invoice: true })), // TODO: Remove the following line once flag is present
    getAllInvoices: async (
      status = null,
      type = null,
      invoiceableId = null,
      invoiceableName = null,
      page = 1,
      per_page = 10
    ): Promise<InvoiceResponses> => {
      return fetch(
        props.svc +
          `/v5/invoices?${makeQueryString(
            {
              status,
              invoiceable_id: invoiceableId,
              invoiceable_type: type,
              invoiceable_name: invoiceableName,
              page,
              per_page,
            },
            ''
          )}`,
        { headers: authJsonHeaders(token) }
      ).then((res) => res.json())
    },
    getInvoiceOrders: async (invoiceId: number, page = 1, per_page = 20): Promise<InvoiceOrdersResponse> => {
      return fetch(`${props.svc}/v5/invoices/${invoiceId}/invoice_orders?${makeQueryString({ page, per_page }, '')}`, {
        headers: authJsonHeaders(token),
      }).then((res) => (res.status >= 200 && res.status < 300 ? res.json() : Promise.reject(res.statusText)))
    },
    getInvoiceOrder: async (invoiceId: number, order_uuid?: string): Promise<InvoiceOrdersResponse> => {
      const res = await fetch(
        `${props.svc}/v5/invoices/${invoiceId}/invoice_orders?${makeQueryString({ order_uuid }, '')}`,
        {
          headers: authJsonHeaders(token),
        }
      )
      return await (res.status >= 200 && res.status < 300 ? res.json() : Promise.reject(res.statusText))
    },
    downloadAll: (status: string, startDate: string | null, endDate: string | null): Promise<void> =>
      fetch(props.svc + '/v5/download_invoices', {
        headers: authJsonHeaders(token),
        method: 'POST',
        body: JSON.stringify({ status, start_date: startDate, end_date: endDate }),
      }).then((res) => (res.status >= 200 && res.status < 300 ? res.json() : Promise.reject(res.statusText))),
    download: (invoice_id: number): Promise<void> =>
      fetch(props.svc + `/v5/invoices/${invoice_id}/download`, {
        headers: authJsonHeaders(token),
        method: 'POST',
      }).then((res) => (res.status >= 200 && res.status < 300 ? res.json() : Promise.reject(res.statusText))),
    downloadInvoiceOrders: (invoice_id: number): Promise<void> =>
      fetch(props.svc + `/v5/invoices/${invoice_id}/download_invoice_orders`, {
        headers: authJsonHeaders(token),
        method: 'POST',
      }).then((res) => (res.status >= 200 && res.status < 300 ? res.json() : Promise.reject(res.statusText))),
    uploadAll: (data: FormData): Promise<void> =>
      fetch(props.svc + '/v5/upload_invoice', {
        headers: authUploadJsonHeaders(token),
        method: 'POST',
        body: data,
      }).then((res) => (res.status >= 200 && res.status < 300 ? res.json() : Promise.reject(res.statusText))),
    markPaid: (invoice_id: number): Promise<Invoice> =>
      fetch(props.svc + `/v5/invoices/${invoice_id}/paid`, {
        headers: authJsonHeaders(token),
        method: 'POST',
      }).then((res) => (res.status >= 200 && res.status < 300 ? res.json() : Promise.reject(res.statusText))),
    updateStatus: (invoice_id: number, newStatus: InvoiceStatus): Promise<Invoice> =>
      fetch(props.svc + `/v5/invoices/${invoice_id}/update_status`, {
        method: 'POST',
        headers: authJsonHeaders(token),
        body: JSON.stringify({ status: newStatus }),
      }).then((res) => (res.status >= 200 && res.status < 300 ? res.json() : Promise.reject(res.statusText))),
    fetchACHStatus: (invoice_id: number): Promise<ACHStatus> =>
      fetch(props.svc + `/v5/invoices/${invoice_id}/ach_transaction`, {
        method: 'POST',
        headers: authJsonHeaders(token),
        body: JSON.stringify({}),
      }).then((res) => (res.status >= 200 && res.status < 300 ? res.json() : Promise.reject(res.statusText))),
    fetchACHLog: (invoice_id: number): Promise<ACHLog> =>
      fetch(props.svc + `/v5/invoices/${invoice_id}/ach_transfer_log`, {
        method: 'POST',
        headers: authJsonHeaders(token),
        body: JSON.stringify({}),
      }).then((res) => (res.status >= 200 && res.status < 300 ? res.json() : Promise.reject(res.statusText))),
  }
  return <Ctx.Provider value={invoiceAPI}>{props.children}</Ctx.Provider>
}

export function useInvoiceAPI(): InvoiceAPI {
  const invoiceAPI = useContext(Ctx)
  return invoiceAPI as InvoiceAPI
}

export const GET_ORDER = gql`
  query LoadOrder($id: ID!) {
    order(id: $id) {
      uuid
      due_time
      created_at
      carts {
        channel_id
        customer {
          name
          email
          phone
        }
        settings {
          service {
            label
          }
          tip {
            id
            label
            value
          }
          curbside_info
        }
      }
      ivr_code
      state
      totals {
        delivery
        discount
        hidden
        service
        subtotal
        tax
        tip
        total
      }
    }
  }
`
